// The examples offered under the search box.
//
// WHY THIS IS COMPUTED RATHER THAN A STRING IN THE COMPONENT (Andrew, 9/20):
// an example that returns an empty calendar is worse than no example at all —
// it's the advertise-7-deliver-6 wrongness the featured chips and the banner's
// upcomingCount already guard against, except here the visitor is being invited
// to make the mistake. Hardcoding "watercolor" works the week it's written and
// quietly stops working when those five classes finish. So the pool below is
// CANDIDATES, and only terms with something actually on get shown.
//
// The pool avoids anything that's already a filter category (Live Music, Open
// Mic, Comedy): the hint's job is to teach that the box finds things the
// checkboxes can't — a craft, a venue, a band — so suggesting a category teaches
// nothing a visitor couldn't get by clicking.
//
// Measured 2026-09-20 over 894 upcoming rows, via the app's own matcher:
//   trivia 119, karaoke 38, yoga 23, hammer & stain 19, pottery 19,
//   farmers market 18, painting 13, watercolor 5, line dancing 3
// Watercolor is Andrew's original example and stays first; it's also the
// thinnest, which is precisely why the floor exists.

import { matchesQuery } from './search'
import { isPastEvent } from './dates'
import type { EventRow } from '../types'

/** Ordered by preference. The first MAX that clear the floor are what show. */
const CANDIDATES = [
  'watercolor',
  'trivia',
  'pottery',
  'karaoke',
  'yoga',
  'painting',
  'farmers market',
  'line dancing',
] as const

/**
 * Upcoming matches a term needs before it's worth suggesting. Three rather than
 * one: a single match can be tonight, and by tomorrow the suggestion is a dead
 * end again. Three buys the hint roughly a week of being true.
 */
const MIN_UPCOMING = 3

/** How many clickable examples the hint offers. Two, so the sentence stays one line. */
const MAX_HINTS = 2

/**
 * Candidate terms that currently have at least MIN_UPCOMING upcoming matches,
 * in pool order, capped at MAX_HINTS.
 *
 * Runs over the whole loaded set rather than the filtered one on purpose: the
 * hint is an invitation to search, so it should mean "the calendar carries
 * this", not "your current filters carry this". A visitor with Bedford ticked
 * should still be told watercolor is a thing they can type.
 */
export function pickSearchHints(events: EventRow[], today: Date): string[] {
  if (events.length === 0) return []
  const upcoming = events.filter((e) => !isPastEvent(e, today))
  const out: string[] = []
  for (const term of CANDIDATES) {
    if (out.length === MAX_HINTS) break
    let hits = 0
    for (const e of upcoming) {
      if (matchesQuery(e, term)) {
        hits++
        if (hits >= MIN_UPCOMING) break
      }
    }
    if (hits >= MIN_UPCOMING) out.push(term)
  }
  return out
}

/**
 * The dismissal flag lives here rather than in the component so SearchHint.tsx
 * exports a component and nothing else (fast refresh), and so FilterDrawer can
 * read it without importing from a sibling component.
 */
export const HINT_DISMISSED_KEY = 'lyh_search_hint_dismissed'

/** Read once for initial state. Storage blocked -> show it; a hint is cheap. */
export function readHintDismissed(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(HINT_DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

/** Remember the dismissal. Silently a no-op when storage is blocked. */
export function markHintDismissed() {
  try {
    window.localStorage.setItem(HINT_DISMISSED_KEY, '1')
  } catch {
    // storage blocked: it still goes away for this page load
  }
}
